<template>
  <div v-if="loading" class="flex items-center justify-center py-10">
    <d-loader />
  </div>
  <div v-else>
    <div v-if="unansweredQuestions.length > 0">
      <p class="text-3xl font-bold mb-0">
        Questions Missing An Answer
      </p>
      <p class="mb-3">
        You must answer all questions before submitting your test for grading.
      </p>
      <div v-for="summary in unansweredQuestions" :key="summary.questionId" class="mb-8">
        <div class="font-bold">
          #{{ summary.orderNumber }} - {{ summary.questionContent }}
        </div>
        <div>
          <router-link :to="{ name: 'question', params: { questionNum: Number.parseInt(summary.orderNumber, 10) }, query: {fromSummary: true} }">
            Edit Answer
          </router-link>
        </div>
      </div>
    </div>
    <p class="text-2xl mb-3">
      Please review your questions before they are submitted for final review.
    </p>
    <div v-for="summary in summaryQuestions" :key="summary.questionId" class="mb-8">
      <div class="font-bold">
        #{{ summary.orderNumber }} - {{ summary.questionContent }}
      </div>
      <div>
        Answered: {{ summary.answerContent }}<br>
        <span v-if="testData.requiresCitations">
          Citation: {{ summary.answerPageNumber }}
        </span>
        <span v-if="testData.requiresPageNumbers">
          Page Number: {{ summary.answerPageNumber }}
        </span>
      </div>
      <div>
        <router-link :to="{ name: 'question', params: { questionNum: Number.parseInt(summary.orderNumber, 10) }, query: {fromSummary: true} }">
          Edit Answer
        </router-link>
      </div>
    </div>
    <div class="flex flex-wrap justify-end py-16">
      <d-tooltip v-if="unansweredQuestions.length > 0" label="You must answer all questions before finalizing this test">
        <d-button disabled>
          Finish Test
        </d-button>
      </d-tooltip>
      <d-button v-else :loading="completingTest" @click="completeTest">
        Finish Test
      </d-button>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import dayjs from 'dayjs';
  import { createNamespacedHelpers } from 'vuex';
  import { queryFirstDrafter } from '@resources/scripts/services/api/filemakerDrafters.api';
  import { queryFirstContact } from '@resources/scripts/services/api/filemakerContacts.api';
  import { TestNames } from '@resources/scripts/models/enums';
  const { mapGetters, mapActions } = createNamespacedHelpers('tests');

  export default {
    data() {
      return {
        completingTest: false,
        summaryQuestions: [],
        loading: true,
      };
    },

    computed: {
      ...mapGetters([
        'testData',
      ]),

      unansweredQuestions() {
        if (this.testData.requiresCitations || this.testData.requiresPageNumbers) {
          return this.summaryQuestions.filter(m => !m.answerContent || !m.answerPageNumber);
        }
        return this.summaryQuestions.filter(m => !m.answerContent);
      },

      computedTestName() {
        switch (this.testData.testType) {
          case 'Policies':
            return TestNames.Policies;
          case 'Standards':
            return TestNames.Standards;
          case 'Drafting':
            return TestNames.Drafting;
          default:
            return '';
        }
      },
    },

    async mounted() {
      await this.getTestSummary();
      this.loading = false;
    },

    methods: {
      ...mapActions([
        'updateTestData',
        'updateQuestions',
      ]),

      async getTestSummary() {
        const { data } = await axios.get(`/api/tests/${this.$route.params.id}/summary`);
        this.summaryQuestions = data;
      },

      async completeTest() {
        this.completingTest = true;
        const { data } = await axios.post(`/api/tests/${this.$route.params.id}/complete`);

        await this.updateFilemaker(data);
        this.updateTestData(data);
        this.updateQuestions(data.questions);
        this.completingTest = false;
      },

      // TODO: Get the drafter application date, then give the expiration date 6 months from that date
      async updateFilemaker(data) {
        if (this.$d2Auth.hasRole('Drafter')) {
          const { data: drafterResult } = await queryFirstDrafter({
            'PDA Contact ID': this.$d2Auth.profile.username,
          });
          // Update test status in Filemaker
          await axios.post('/api/drafters/tests', {
            'PDA Contact ID': this.$d2Auth.profile.username,
            Date_Applied: dayjs(drafterResult['Application Date']).format('MM/DD/YYYY'),
            Test_Date: dayjs().format('MM/DD/YYYY'),
            Expiration_Date: dayjs(drafterResult['Application Expiration Date']).format('MM/DD/YYYY'),
            'Test Score': (data.finalPercentage * 100).toFixed(2),
            'Test Result': data.passed ? 'Pass' : 'Fail',
          });
        }
        if (this.$d2Auth.hasRole('QCP Liaison')) {
          const { data: contactResult } = await queryFirstContact({
            'Contact ID': this.$d2Auth.profile.username,
          });
          // Update test status in Filemaker
          await axios.post('/api/test-organization/test-results', {
            organization_id: contactResult['Organization ID'],
            user_id: this.$d2Auth.profile.username,
            test: this.computedTestName,
            date_expiration: data.passed ? dayjs().add(3, 'years').format('MM/DD/YYYY') : '',
            score: (data.finalPercentage * 100).toFixed(2),
            date_passed: data.passed ? dayjs().format('MM/DD/YYYY') : '',
          });
        }
      },
    },
  };
</script>